import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router-dom";
import Layout from "../components/feature/Layout";

// Eagerly loaded — shown immediately on first paint
import Landing from "../pages/landing/page";
import Login from "../pages/auth/login/page";
import SignUp from "../pages/auth/signup/page";

// Lazy-loaded — split into separate chunks, loaded on demand
const NotFound         = lazy(() => import("../pages/NotFound"));
const Home             = lazy(() => import("../pages/home/page"));
const Payments         = lazy(() => import("../pages/payments/page"));
const AddPayment       = lazy(() => import("../pages/payments/add/page"));
const PaymentDetail    = lazy(() => import("../pages/payments/detail/page"));
const EditPayment      = lazy(() => import("../pages/payments/edit/page"));
const Subscriptions    = lazy(() => import("../pages/subscriptions/page"));
const Documents        = lazy(() => import("../pages/documents/page"));
const Calendar         = lazy(() => import("../pages/calendar/page"));
const Analytics        = lazy(() => import("../pages/analytics/page"));
const Profile          = lazy(() => import("../pages/profile/page"));
const ReminderPrefs    = lazy(() => import("../pages/profile/reminders/page"));
const Settings         = lazy(() => import("../pages/profile/settings/page"));
const PersonalInfo     = lazy(() => import("../pages/profile/settings/personal/page"));
const ChangePassword   = lazy(() => import("../pages/profile/settings/password/page"));
const TwoFactor        = lazy(() => import("../pages/profile/settings/two-factor/page"));
const Devices          = lazy(() => import("../pages/profile/settings/devices/page"));
const UpgradePro       = lazy(() => import("../pages/profile/upgrade/page"));
const ForgotPassword   = lazy(() => import("../pages/auth/forgot-password/page"));
const ResetPassword    = lazy(() => import("../pages/auth/reset-password/page"));
const AdminLogin       = lazy(() => import("../pages/admin/login/page"));
const AdminDashboard   = lazy(() => import("../pages/admin/dashboard/page"));
const PrivacyPolicy    = lazy(() => import("../pages/legal/PrivacyPolicy"));
const Terms            = lazy(() => import("../pages/legal/Terms"));
const ContactPage      = lazy(() => import("../pages/contact/page"));

const Spinner = () => (
  <div className="flex items-center justify-center h-[60vh]">
    <i className="ri-loader-4-line animate-spin text-indigo-500 text-2xl"></i>
  </div>
);

const wrap = (el: React.ReactElement) => <Suspense fallback={<Spinner />}>{el}</Suspense>;

const routes: RouteObject[] = [
  { path: "/",                      element: <Landing /> },
  { path: "/auth/login",            element: <Login /> },
  { path: "/auth/signup",           element: <SignUp /> },
  { path: "/auth/forgot-password",  element: wrap(<ForgotPassword />) },
  { path: "/auth/reset-password",   element: wrap(<ResetPassword />) },
  { path: "/admin/login",           element: wrap(<AdminLogin />) },
  { path: "/admin/dashboard",       element: wrap(<AdminDashboard />) },
  { path: "/privacy",               element: wrap(<PrivacyPolicy />) },
  { path: "/terms",                 element: wrap(<Terms />) },
  { path: "/contact",               element: wrap(<ContactPage />) },
  {
    element: <Layout />,
    children: [
      { path: "/home",                          element: wrap(<Home />) },
      { path: "/payments",                      element: wrap(<Payments />) },
      { path: "/payments/add",                  element: wrap(<AddPayment />) },
      { path: "/payments/:id",                  element: wrap(<PaymentDetail />) },
      { path: "/payments/:id/edit",             element: wrap(<EditPayment />) },
      { path: "/subscriptions",                 element: wrap(<Subscriptions />) },
      { path: "/documents",                     element: wrap(<Documents />) },
      { path: "/calendar",                      element: wrap(<Calendar />) },
      { path: "/analytics",                     element: wrap(<Analytics />) },
      { path: "/profile",                       element: wrap(<Profile />) },
      { path: "/profile/reminders",             element: wrap(<ReminderPrefs />) },
      { path: "/profile/settings",              element: wrap(<Settings />) },
      { path: "/profile/settings/personal",     element: wrap(<PersonalInfo />) },
      { path: "/profile/settings/password",     element: wrap(<ChangePassword />) },
      { path: "/profile/settings/two-factor",   element: wrap(<TwoFactor />) },
      { path: "/profile/settings/devices",      element: wrap(<Devices />) },
      { path: "/profile/upgrade",               element: wrap(<UpgradePro />) },
      { path: "*",                              element: wrap(<NotFound />) },
    ],
  },
];

export default routes;
